/**
 * Замер: запуск и наблюдение.
 *
 * Прогресс тянется **опросом**, а не потоком событий — так решено в контракте:
 * `GET /runs/{runId}` опрашивается раз в три секунды, рекомендованный интервал
 * приходит заголовком `Retry-After`, а в терминальном ответе его нет — это и
 * есть команда остановиться (docs/API.md §8). Опрос идёт, только пока замер
 * действительно идёт: висящий таймер на завершённом прогоне это лишние деньги
 * и лишний шум в логах.
 *
 * Полоса кормится настоящими счётчиками прогона. Полоса, которая едет сама по
 * себе, в продукте про измерения — обман: пока сетка не размечена, показываются
 * шаги конвейера, и ни одного неопределённого индикатора (app-kit.md §5).
 *
 * Частичный отказ раскрывается на том же экране и рядом с числами. Движок,
 * посыпавшийся серией ошибок, отключается предохранителем до конца замера —
 * это **сбой источника, а не падение бренда**, и говорится это прямо. Молча
 * выродившееся число — худшее, что может сделать интерфейс замера.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, idempotencyKey, requestWith, type Page } from '../../api/client';
import { ApiError } from '../../api/problem';
import { isPreparing } from '../../ui/preparation';
import { useAccess } from '../../session';
import { useScope } from '../../scope';
import { useLoad } from '../../ui/load';
import { Confirm } from '../../ui/fields';
import { Empty, ErrorNotice, Progress, Skeleton } from '../../ui/state';
import { Measuring, pickBrand, PartialRun, QuotaExhausted } from '../../ui/screen-states';
import { isRunning, runKind, runState } from '../../ui/labels';
import {
  countOf,
  coverage,
  coverageOf,
  dateProse,
  int,
  percent,
} from '@shared/src/format';
import type { components } from '../../api/schema';
import { DOUBLE_RUN } from '@shared/src/product';

type Run = components['schemas']['Run'];
type RunDetail = components['schemas']['RunDetail'];
type Engine = components['schemas']['EngineWithHealth'];
type Quotas = components['schemas']['OrganizationQuotas'];

/** Интервал опроса по умолчанию — три секунды, как в контракте. */
const POLL_FALLBACK = 3;

export function Runs() {
  const scope = useScope();
  const access = useAccess();
  const brandId = scope.brandId;

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<unknown>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<RunDetail | null>(null);
  const [version, setVersion] = useState(0);

  const runs = useLoad<Page<Run> | null>(
    `runs:${brandId ?? 'none'}:${version}`,
    (signal) =>
      brandId
        ? api.get<Page<Run>>('/runs', { signal, query: { brand_id: brandId, limit: 50 } })
        : Promise.resolve(null),
  );
  const engines = useLoad<Engine[]>('engines', (signal) =>
    api.get<Engine[]>('/engines', { signal }),
  );
  const quotas = useLoad<Quotas | null>('quotas', (signal) =>
    access.reaches('GET', '/organizations/current/quotas')
      ? api.get<Quotas>('/organizations/current/quotas', { signal })
      : Promise.resolve(null),
  );

  const items =
    runs.state.kind === 'ready' && runs.state.data ? runs.state.data.items : [];
  const activeRun = items.find((run) => isRunning(run.state)) ?? null;

  // Раскрыт либо выбранный руками прогон, либо идущий: он важнее истории.
  const watchId = activeRun?.id ?? openId;
  const watched = useRunPolling(watchId, () => setVersion((value) => value + 1));

  const engineTitle = useMemo(() => {
    const list = engines.state.kind === 'ready' ? engines.state.data : [];
    return (code: string) => list.find((engine) => engine.code === code)?.title ?? code;
  }, [engines.state]);

  const quota = quotas.state.kind === 'ready' ? quotas.state.data : null;
  /* Пока пишутся вопросы, спрашивать движки НЕЧЕМ. Замер по ещё не написанному
     ядру вернул бы нулевую видимость и записал бы её первой точкой графика —
     клиент увидел бы ноль там, где продукт просто не успел задать вопрос. */
  const preparing = isPreparing(scope.brand);
  /* Право на замер — это про роль, и отмена уже идущего прогона держится на нём
     же: подготовка нового набора не отнимает права остановить то, что бежит. */
  const mayRun = access.reaches('POST', '/runs') && access.can('runs.start');
  const canStart = mayRun && !preparing;

  async function start(kind: 'manual' | 'repair') {
    if (!brandId) return;
    setStarting(true);
    setStartError(null);
    try {
      const run = await api.post<Run>(
        '/runs',
        { brand_id: brandId, kind },
        { idempotencyKey: idempotencyKey() },
      );
      setOpenId(run.id);
      setVersion((value) => value + 1);
    } catch (cause) {
      setStartError(cause);
    } finally {
      setStarting(false);
    }
  }

  async function cancel(run: RunDetail) {
    setCancelling(null);
    setStarting(true);
    setStartError(null);
    try {
      await api.post<Run>(`/runs/${run.id}/cancel`);
      setVersion((value) => value + 1);
    } catch (cause) {
      setStartError(cause);
    } finally {
      setStarting(false);
    }
  }

  const lastFinished = items.find((run) => !isRunning(run.state));
  const canRepair =
    lastFinished?.state === 'partial' || (lastFinished?.failed_tasks ?? 0) > 0;

  const head = (
    <div className="screen-head">
      <div>
        <h1>Прогоны</h1>
        <p className="stamp">
          {scope.brand ? `${scope.brand.name} · ` : ''}
          {DOUBLE_RUN}
        </p>
      </div>
      {canStart && brandId ? (
        <div className="row-actions">
          <button
            className="btn btn-primary btn-sm"
            type="button"
            disabled={starting || activeRun !== null}
            onClick={() => void start('manual')}
          >
            Запустить замер
          </button>
          {canRepair ? (
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              disabled={starting || activeRun !== null}
              onClick={() => void start('repair')}
            >
              Заполнить пропуски
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  if (scope.state.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }

  if (!brandId) {
    return (
      <>
        {head}
        <Empty
          eyebrow="Выберите бренд"
          title="Замер идёт по конкретному бренду"
          action={
            scope.state.kind === 'ready' && scope.liveBrands.length === 0 ? (
              <Link className="btn btn-primary" to="/brands/new">
                Завести бренд
              </Link>
            ) : undefined
          }
        >
          Один бренд — один активный замер каждого вида. {pickBrand()}
        </Empty>
      </>
    );
  }

  const startProblem = startError instanceof ApiError ? startError.problem : null;
  const blocked =
    startProblem?.code === 'quota_exceeded' ||
    startProblem?.code === 'scan_blocked' ||
    startProblem?.code === 'payment_required';

  if (runs.state.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }

  if (runs.state.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice
          error={runs.state.error}
          action={
            <button className="btn btn-ghost btn-sm" type="button" onClick={runs.reload}>
              Повторить
            </button>
          }
        />
      </>
    );
  }

  return (
    <>
      {head}

      {blocked ? (
        <QuotaExhausted
          quotas={quota}
          canPay={access.reaches('POST', '/billing/subscription/plan')}
          action={
            <a className="btn btn-primary" href="/plan">
              Открыть тариф
            </a>
          }
        />
      ) : startError ? (
        <ErrorNotice error={startError} />
      ) : null}

      {watched.error ? <ErrorNotice error={watched.error} /> : null}

      {watched.run ? (
        <RunPanel
          run={watched.run}
          engineTitle={engineTitle}
          canCancel={mayRun}
          onCancel={() => setCancelling(watched.run)}
        />
      ) : null}

      {items.length === 0 ? (
        <Empty
          eyebrow="Замера ещё не было"
          title={
            scope.brand
              ? `${scope.brand.name} мы пока не спрашивали`
              : 'Замеров ещё не было'
          }
          action={
            canStart ? (
              <button
                className="btn btn-primary"
                type="button"
                disabled={starting}
                onClick={() => void start('manual')}
              >
                Запустить первый замер
              </button>
            ) : undefined
          }
        >
          {DOUBLE_RUN} Ответы разберём. Первый замер занимает несколько минут — после него
          появятся индекс ИИ-видимости и разбор по движкам.
        </Empty>
      ) : (
        <div className="tbl-wrap">
          <table className="mini">
            <caption>
              Прогоны бренда, новые сверху ·{' '}
              {countOf(items.length, 'прогон', 'прогона', 'прогонов')}
            </caption>
            <thead>
              <tr>
                <th scope="col">Вид</th>
                <th scope="col">Состояние</th>
                <th scope="col" className="tnum-col">
                  Сетка
                </th>
                <th scope="col" className="tnum-col">
                  Сбоев
                </th>
                <th scope="col">Снимок</th>
                <th scope="col">Запущен</th>
                <th scope="col">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {items.map((run) => (
                <tr key={run.id} className={run.id === watchId ? 'picked' : undefined}>
                  <td>{runKind(run.kind)}</td>
                  <td>
                    <span className={run.state === 'partial' ? 'warn' : undefined}>
                      {runState(run.state)}
                    </span>
                  </td>
                  <td className="tnum tnum-col">
                    {coverage(run.done_tasks ?? 0, run.planned_tasks ?? 0)}
                  </td>
                  <td className="tnum tnum-col">
                    {run.failed_tasks ? (
                      int(run.failed_tasks)
                    ) : (
                      <span className="dim">—</span>
                    )}
                  </td>
                  <td>
                    {run.snapshot_date ? (
                      dateProse(run.snapshot_date)
                    ) : (
                      <span className="dim">Сетка не закрылась</span>
                    )}
                  </td>
                  <td>{run.created_at ? dateProse(run.created_at) : ''}</td>
                  <td>
                    <button
                      type="button"
                      className="linkish"
                      onClick={() => setOpenId(run.id === watchId ? null : run.id)}
                    >
                      {run.id === watchId ? 'Свернуть' : 'Раскрыть'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Confirm
        open={cancelling !== null}
        title="Остановить замер"
        action="Остановить замер"
        danger
        onCancel={() => setCancelling(null)}
        onConfirm={() => cancelling && void cancel(cancelling)}
      >
        {/* «Незахваченные задачи», «до конца аренды» — слова из машинного зала,
            и стояли они в диалоге, который останавливает работу за деньги
            клиента. Смысл тот же: то, что уже спрашивается, дойдёт до конца, всё
            остальное снимется. */}
        <p>
          Вопросы, которые движки уже спрашивают, дойдут до конца — они оплачены, и их
          ответы мы сохраним. Всё, что ещё не начато, снимется сразу. Сетка останется
          неполной, и в историю такой прогон не попадёт.
        </p>
      </Confirm>
    </>
  );
}

/* ─────────────────────────── панель одного прогона ───────────────────────── */

function RunPanel({
  run,
  engineTitle,
  canCancel,
  onCancel,
}: {
  run: RunDetail;
  engineTitle: (code: string) => string;
  canCancel: boolean;
  onCancel: () => void;
}) {
  const running = isRunning(run.state);

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <span className="eyebrow">{runKind(run.kind)}</span>
          <h2 className="panel-h">{runState(run.state)}</h2>
        </div>
        {running && canCancel ? (
          <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel}>
            Остановить замер
          </button>
        ) : null}
      </div>

      {running ? <Measuring run={run} /> : null}

      {!running && (run.state === 'partial' || (run.failed_tasks ?? 0) > 0) ? (
        <PartialRun run={run} engineTitle={engineTitle} />
      ) : null}

      {run.state === 'completed' ? (
        <p className="stamp good">
          Сетка закрыта целиком:{' '}
          {coverageOf(
            run.done_tasks ?? 0,
            run.planned_tasks ?? 0,
            'задача',
            'задачи',
            'задач',
          )}
          , снимок за{' '}
          {run.snapshot_date ? dateProse(run.snapshot_date) : 'день закрытия сетки'}.
        </p>
      ) : null}

      {run.state === 'cancelled' ? (
        <p className="stamp">
          Замер остановлен вручную. Сделанные запросы сохранены, но сетка неполная — в
          историю этот прогон не пойдёт.
        </p>
      ) : null}

      {(run.engines ?? []).length > 0 ? (
        <div className="tbl-wrap">
          <table className="mini">
            <caption>Движки этого прогона</caption>
            <thead>
              <tr>
                <th scope="col">Движок</th>
                <th scope="col" className="tnum-col">
                  Сделано
                </th>
                <th scope="col" className="tnum-col">
                  Сбоев
                </th>
                <th scope="col">Ход</th>
                <th scope="col">Состояние</th>
              </tr>
            </thead>
            <tbody>
              {(run.engines ?? []).map((engine) => {
                const share =
                  engine.tasks_total > 0
                    ? ((engine.tasks_done + engine.tasks_failed) / engine.tasks_total) *
                      100
                    : 0;
                return (
                  <tr key={engine.engine_code}>
                    <td>
                      <span className="row-title">{engineTitle(engine.engine_code)}</span>
                      {engine.model_version ? (
                        <span className="stamp">{engine.model_version}</span>
                      ) : null}
                    </td>
                    <td className="tnum tnum-col">
                      {coverage(engine.tasks_done, engine.tasks_total)}
                    </td>
                    <td className="tnum tnum-col">
                      {engine.tasks_failed ? (
                        int(engine.tasks_failed)
                      ) : (
                        <span className="dim">—</span>
                      )}
                    </td>
                    <td className="cell-bar">
                      <Progress
                        done={engine.tasks_done + engine.tasks_failed}
                        total={engine.tasks_total}
                        caption={`${engineTitle(engine.engine_code)}: ${percent(share)}`}
                      />
                    </td>
                    <td>
                      {engine.circuit_broken ? (
                        <span className="warn">
                          Движок отключён
                          <span className="stamp">
                            {engine.circuit_reason ??
                              'Серия ошибок подряд, движок отключён до конца замера'}
                          </span>
                        </span>
                      ) : engine.tasks_done + engine.tasks_failed >=
                        engine.tasks_total ? (
                        'Закрыт'
                      ) : (
                        <span className="dim">Идёт</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Про сработавший предохранитель говорит `PartialRun` — повторять это
          второй раз на том же экране значит удваивать тревогу на ровном месте.
          Здесь остаётся только то, чего он не знает: потолок расходов. */}
      {run.cost_capped ? (
        <p className="stamp warn">
          {/* «Потолок расходов» без хозяина читается как перерасход клиента:
              он лезет проверять тариф, а предел здесь наш и на его квоту не
              влияет. Заодно сказано, что делать: ничего. */}
          Замер упёрся в наш предел расходов — часть клеток сетки останется незаполненной.
          Это наше ограничение, из вашей квоты оно ничего не берёт. Следующий замер пойдёт
          как обычно.
        </p>
      ) : null}
    </section>
  );
}

/* ────────────────────────────────── опрос ────────────────────────────────── */

interface Polling {
  run: RunDetail | null;
  error: unknown;
}

/**
 * Опрос состояния прогона.
 *
 * Интервал задаёт сервер заголовком `Retry-After`; отсутствие заголовка в
 * терминальном ответе — сигнал остановиться, а не повод подставить своё
 * значение. Когда опрос закончился, список прогонов перечитывается: строка в
 * истории должна встать сама, без нажатия «обновить».
 *
 * Ответ хранится вместе с идентификатором прогона, к которому он относится, —
 * тогда «показывать нечего» считается сравнением, а не сбрасывается вручную из
 * эффекта. Кадра, где панель одного прогона стоит под заголовком другого, при
 * таком устройстве не бывает.
 */
function useRunPolling(runId: string | null, onSettled: () => void): Polling {
  const [answer, setAnswer] = useState<{
    id: string;
    run: RunDetail | null;
    error: unknown;
  } | null>(null);

  // Обработчик завершения меняется каждой перерисовкой, а перезапускать опрос
  // из-за этого нельзя: он бы стартовал заново каждые три секунды.
  const settled = useRef(onSettled);
  useEffect(() => {
    settled.current = onSettled;
  });

  useEffect(() => {
    if (!runId) return;

    const abort = new AbortController();
    let timer: number | undefined;
    let wasRunning = false;

    const tick = () => {
      requestWith<RunDetail>(`/runs/${runId}`, { signal: abort.signal })
        .then(({ body, retryAfter }) => {
          setAnswer({ id: runId, run: body, error: null });
          if (isRunning(body.state)) {
            wasRunning = true;
            timer = window.setTimeout(tick, (retryAfter ?? POLL_FALLBACK) * 1000);
          } else if (wasRunning) {
            // Замер закончился на наших глазах — история устарела.
            wasRunning = false;
            settled.current();
          }
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setAnswer({ id: runId, run: null, error });
        });
    };

    tick();
    return () => {
      abort.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [runId]);

  const fresh = answer && answer.id === runId ? answer : null;
  return { run: fresh?.run ?? null, error: fresh?.error ?? null };
}
